import {
  isMergeJoin,
  isBranchSplit,
  isConditionOrGoalSplit,
  isExitLoopStep,
  getExitLoopTarget,
  isGoalOrCondition,
  isLoopStep,
  isLoopStepNext,
} from 'pages/AutomationEngine/store/ts-utilities';
import type { Step } from 'pages/AutomationEngine/types';
import type { ExecutionHistoryStep } from './types';

// A skipped loop never runs its body, so every step between the loop and the
// Next/Finish that closes it is unreachable.
const isSkippedLoop = (parentStep?: Partial<Step>) =>
  Boolean(
    parentStep && isLoopStep(parentStep) && parentStep.shouldSkipExecution
  );

// An Exit Loop jumps straight to the step after the loop it exits, so anything
// hanging off it never runs. Debug-skipping the step restores the normal path,
// which is what lets a debug run step past it. This covers the steps that sit
// directly after the Exit Loop, up to the Next/Finish that closes the loop
// around them - the case the AC describes.
const isTakenExitLoop = (parentStep?: Partial<Step>) =>
  Boolean(
    parentStep && isExitLoopStep(parentStep) && !parentStep.shouldSkipExecution
  );

// Whether this exit jumps to the given step rather than past it. An exit lands
// on the Next/Finish of the loop it names - that step is its destination, the
// one the engine marks to end the loop early. Every other exit still cuts the
// path, which is what stops an exit sitting upstream of a whole nested loop
// from being waved through on the way to that loop's Next/Finish.
const landsOn = (step: Partial<Step>) => (exit: Partial<Step>) =>
  isLoopStepNext(step) &&
  getExitLoopTarget(exit)?.loopStepId === step.loopParent;

type StepIndex = {
  stepsById: Record<string, Partial<Step>>;
  childrenByParent: Record<string, Partial<Step>[]>;
  nextFinishByLoop: Record<string, Partial<Step>>;
};

const buildIndex = (steps: Step[]): StepIndex =>
  steps.reduce<StepIndex>(
    (acc, step) => {
      acc.stepsById[step.id] = step;

      if (step.parentKey) {
        (acc.childrenByParent[step.parentKey] ??= []).push(step);
      }

      if (isLoopStepNext(step) && step.loopParent) {
        acc.nextFinishByLoop[step.loopParent] = step;
      }

      return acc;
    },
    { stepsById: {}, childrenByParent: {}, nextFinishByLoop: {} }
  );

const isSplit = (step: Partial<Step>) =>
  isConditionOrGoalSplit(step) || isBranchSplit(step);

// Which path of a goal/condition a step sits on. fromLabel is the one the store
// keeps current as steps are dropped and pasted around; parentCondition only
// catches up on save, when stepForApi rewrites it from fromLabel. Reading the
// stale one first makes a step pasted onto the other branch still look like it
// is on the branch it was copied from.
const branchLabelOf = (step: Partial<Step>) =>
  step.fromLabel || step.parentCondition;

// Whether an exit carries the path clear of the given loop. The loops a step
// sits in are recorded innermost first, so an exit escapes this one only when
// the loop it jumps to sits further out: naming this loop lands on its
// Next/Finish, and naming one nested inside it carries on within it.
const escapesLoop = (loopId: string) => (step: Partial<Step>) => {
  if (!isTakenExitLoop(step)) {
    return false;
  }

  const loops = step.loopVariables ?? [];
  const target = getExitLoopTarget(step)?.loopStepId;
  const targetDepth = loops.findIndex((v) => v.loopStepId === target);
  const loopDepth = loops.findIndex((v) => v.loopStepId === loopId);

  return targetDepth > -1 && loopDepth > -1 && targetDepth > loopDepth;
};

// The step a path resumes on once the loop that opens here has closed.
const afterLoop = (loopStep: Partial<Step>, index: StepIndex) => {
  const next = loopStep.id ? index.nextFinishByLoop[loopStep.id] : undefined;
  return next?.id ? (index.childrenByParent[next.id] ?? [])[0] : undefined;
};

const loopBody = (loopStep: Partial<Step>, index: StepIndex) =>
  (loopStep.id ? index.childrenByParent[loopStep.id] ?? [] : []).find(
    (child) => !isLoopStepNext(child)
  );

// Whether every path through this loop's body leaves the loop altogether, so
// the steps that run after it never do. An exit naming this loop does not
// count - that one ends the loop early and carries straight on to them.
const loopBodyAlwaysEscapes = (
  loopStep: Partial<Step>,
  index: StepIndex,
  lands: (exit: Partial<Step>) => boolean = () => false
) =>
  Boolean(
    loopStep.id &&
      pathExitsLoop(
        loopBody(loopStep, index),
        index,
        (candidate) =>
          escapesLoop(loopStep.id!)(candidate) && !lands(candidate),
        index.nextFinishByLoop[loopStep.id]?.id
      )
  );

// Whether every path out of this step hits a qualifying Exit Loop before it can
// reach whatever follows the group the step sits in. Walking the path rather
// than the subtree is what keeps a goal/condition honest: its branches rejoin
// at its merge, so the group only counts as exiting when all of them do.
const pathExitsLoop = (
  start: Partial<Step> | undefined,
  index: StepIndex,
  counts: (step: Partial<Step>) => boolean = isTakenExitLoop,
  stopAt?: string
): boolean => {
  const seen = new Set<string>();
  let current = start;

  while (current?.id && !seen.has(current.id)) {
    if (current.id === stopAt) {
      // the path ran to the end of the loop body and closed it normally
      return false;
    }

    seen.add(current.id);

    if (counts(current)) {
      return true;
    }

    const children = index.childrenByParent[current.id] ?? [];
    const merge = children.find(isMergeJoin);

    if (merge?.id) {
      if (groupExitsLoop(merge, index, counts, stopAt)) {
        return true;
      }
      // the branches rejoined, so the path picks up after the merge
      current = (index.childrenByParent[merge.id] ?? [])[0];
      continue;
    }

    if (children.some(isSplit)) {
      // an unmerged goal/condition - its paths never rejoin, so there is
      // nothing after it on this path to reach
      return false;
    }

    if (isLoopStep(current)) {
      // a nested loop runs its own body, so the path only breaks here if that
      // body carries it past whatever we are judging - otherwise it resumes
      // once the nested loop closes
      if (
        pathExitsLoop(
          loopBody(current, index),
          index,
          counts,
          index.nextFinishByLoop[current.id]?.id
        )
      ) {
        return true;
      }
      current = afterLoop(current, index);
      continue;
    }

    current = children[0];
  }

  return false;
};

// Whether every branch feeding this merge exits, which is what makes the merge
// - and everything after it - unreachable. A branch holding no steps flows
// straight through, so a group is only dead when every path it offers is
// accounted for.
const groupExitsLoop = (
  mergeStep: Partial<Step>,
  index: StepIndex,
  counts: (step: Partial<Step>) => boolean = isTakenExitLoop,
  stopAt?: string
): boolean => {
  const group = mergeStep.parentKey
    ? index.stepsById[mergeStep.parentKey]
    : undefined;
  const split = group?.id
    ? (index.childrenByParent[group.id] ?? []).find(isSplit)
    : undefined;

  if (!split?.id) {
    return false;
  }

  const branchRoots = index.childrenByParent[split.id] ?? [];
  const paths = new Set(branchRoots.map(branchLabelOf));

  // one of the paths holds no steps at all, so it reaches the merge untouched
  if (paths.size < 2) {
    return false;
  }

  return branchRoots.every((root) =>
    pathExitsLoop(root, index, counts, stopAt)
  );
};

// Whether a step can never be reached in this execution, because an ancestor
// was skipped onto another path, archived the record, or is a skipped loop.
//
// Note the tree here is the one the flow renders: a Next/Finish hangs off the
// end of its loop's body, and the steps that run after the loop hang off the
// Next/Finish. Climbing out of a loop therefore has to jump the whole body,
// which is what the Next/Finish case below does.
export const isStepUnreachable = (
  step: ExecutionHistoryStep,
  steps: Step[]
): boolean => {
  if (step.shouldSkipExecution || !step.parentKey) {
    return false;
  }
  const index = buildIndex(steps);
  const stepsByIds = index.stepsById;

  // An exit that jumps to this step is a path to it rather than one that skips
  // it, so it is left out of every exit-driven reason below. Exits that jump
  // elsewhere still count, and so does every other way of being unreachable.
  const landsHere = landsOn(step);
  const cuts = (candidate: Partial<Step>) =>
    isTakenExitLoop(candidate) && !landsHere(candidate);

  const branchSkipLookup = steps
    .filter((s) => isGoalOrCondition(s))
    .reduce<Record<string, any>>((acc, branch) => {
      let continueWithBranch = '';
      if (branch.parentKey) {
        continueWithBranch = branch.continueWithBranch || '';

        let parentStep = stepsByIds[branch.parentKey];
        let childStep = branch;
        while (parentStep) {
          if (isMergeJoin(parentStep)) {
            if (groupExitsLoop(parentStep, index, cuts)) {
              // every branch exited the loop, so neither path of this one runs
              continueWithBranch = 'both';
              break;
            }

            if (!parentStep.parentKey) {
              break;
            }

            // skip pass merge joins to infront of the split
            const split = stepsByIds[parentStep.parentKey];
            if (!split.parentKey) {
              break;
            }

            childStep = parentStep as ExecutionHistoryStep;
            parentStep = stepsByIds[split.parentKey];
            continue;
          }

          if (isLoopStepNext(parentStep)) {
            // climbing out from after a loop: jump the body, which did not
            // run on the way to this step
            const loopStep = stepsByIds[parentStep.loopParent || ''];

            if (loopStep && loopBodyAlwaysEscapes(loopStep, index, landsHere)) {
              // every pass through that loop left it behind, so nothing after
              // it - this branch included - is ever reached
              continueWithBranch = 'both';
              break;
            }

            if (!loopStep?.parentKey) {
              break;
            }
            childStep = loopStep as ExecutionHistoryStep;
            parentStep = stepsByIds[loopStep.parentKey];
            continue;
          }

          if (isSkippedLoop(parentStep) || cuts(parentStep)) {
            // the branch sits in a loop that never runs, or after an Exit Loop
            // that always jumps past it, so neither of its paths can be reached
            continueWithBranch = 'both';
            break;
          }

          if (parentStep.shouldSkipExecution) {
            continueWithBranch = parentStep.continueWithBranch || '';
          } else if (
            isConditionOrGoalSplit(parentStep) &&
            parentStep.parentKey
          ) {
            // need to look at the branch parent
            const branchParent = stepsByIds[parentStep.parentKey];
            if (branchParent.shouldSkipExecution) {
              const unreachable = Boolean(
                branchParent.continueWithBranch &&
                  branchParent.continueWithBranch !== branchLabelOf(childStep)
              );
              if (unreachable) {
                continueWithBranch = 'both';
              }
            }
            break;
          }

          if (!parentStep.parentKey) {
            break;
          }

          childStep = parentStep as ExecutionHistoryStep;
          parentStep = stepsByIds[parentStep.parentKey];
        }
      } else {
        // at the route
        continueWithBranch = branch.continueWithBranch || '';
      }
      acc[branch.id] = continueWithBranch;
      return acc;
    }, {});

  let parentStep = stepsByIds[step.parentKey];
  let childStep = step;
  let unreachable = false;

  while (parentStep) {
    {
      if (isMergeJoin(parentStep)) {
        if (groupExitsLoop(parentStep, index, cuts)) {
          // every branch of the group exited the loop, so nothing downstream of
          // where they rejoin can run
          unreachable = true;
          break;
        }

        childStep = parentStep as ExecutionHistoryStep;
        if (!parentStep.parentKey) {
          break;
        }
        // skip pass merge joins to infront of the split
        const split = stepsByIds[parentStep.parentKey];
        if (!split.parentKey) {
          break;
        }
        parentStep = stepsByIds[split.parentKey];
        continue;
      }

      if (isLoopStepNext(parentStep)) {
        // climbing out from after a loop: jump the body, which did not run
        // on the way to this step
        const loopStep = stepsByIds[parentStep.loopParent || ''];

        if (loopStep && loopBodyAlwaysEscapes(loopStep, index, landsHere)) {
          // every pass through that loop exited to somewhere beyond it, so the
          // steps that run after it never do
          unreachable = true;
          break;
        }

        if (!loopStep?.parentKey) {
          break;
        }
        childStep = loopStep as ExecutionHistoryStep;
        parentStep = stepsByIds[loopStep.parentKey];
        continue;
      }

      if (isSkippedLoop(parentStep) || cuts(parentStep)) {
        unreachable = true;
        break;
      }

      if (parentStep.shouldSkipExecution) {
        unreachable = Boolean(
          parentStep.continueWithBranch &&
            parentStep.continueWithBranch !== branchLabelOf(childStep)
        );
      } else if (isConditionOrGoalSplit(parentStep) && parentStep.parentKey) {
        // need to look at the branch parent
        const branchParent = stepsByIds[parentStep.parentKey];

        if (branchParent?.id && branchSkipLookup[branchParent?.id] === 'both') {
          unreachable = true;
          break;
        }

        if (branchParent.shouldSkipExecution) {
          unreachable = Boolean(
            branchParent.continueWithBranch &&
              branchParent.continueWithBranch !== branchLabelOf(childStep)
          );
        }
        break;
      } else if (parentStep.type === 'archive_record') {
        unreachable = Boolean(
          parentStep.details?.recordSource === 'this_record'
        );
      }

      if (unreachable) {
        break;
      }

      if (!parentStep.parentKey) {
        break;
      }
      childStep = parentStep as ExecutionHistoryStep;
      parentStep = stepsByIds[parentStep.parentKey];
    }
  }

  return unreachable;
};
